// Clustering skeletons into candidate behaviours, and grading them.
//
// Grading is deliberately NOT a single score. Every scalar ranking this
// pipeline has used so far has been wrong in an instructive way: ranking by
// volume put a dev-environment VM launcher above a continuous reverse-
// engineering pipeline, and correcting it needed a hand-maintained list of
// families to demote — exactly the taste that is supposed to be leaving the
// loop. So each cluster carries its measurements and the judge weighs them.
//
// The measurements chosen are the ones derivable from the corpus alone:
//
//   recurrence     how many instances, and how many sessions/endpoints/tenants
//   concentration  instances per session — dense in few sessions reads as a
//                  campaign, thin across many reads as a habit. Both can be
//                  worth a lens; they are different KINDS of worth.
//   consequence    does it change state outside the sandbox, or leave the host
//   spread         one tenant is not a defect. A behaviour dense in one tenant
//                  and absent elsewhere is precisely what a per-tenant catalog
//                  is for; spread only argues about promotion to a shared core.
//
// Deliberately absent: any notion of "interesting". That is the judge's call,
// and encoding it here is how the last version acquired a demotion list.

import { skeletonizeShell, skeletonKey, foldMutates } from "./skeleton.js";

const MAX_INSTANCES_KEPT = 24;
// Distinct values tracked per placeholder. Past the cap new values still COUNT
// (total keeps moving) but are no longer enumerated; `truncated` says so, because
// "40 distinct" and "40 distinct that we stopped counting at" are different
// evidence about spread.
const MAX_SLOT_VALUES = 40;
const TOP_SLOT_VALUES = 5;

/**
 * @param {object[]} calls  {command, sessionId, endpointId, tenantId, ts, promptId}
 * @param {object}   opts
 *   minInstances  clusters below this are held back as the long tail (default 3)
 *   mergeNear     fold clusters whose skeletons differ by one token (default true)
 */
export function clusterCalls(calls, { minInstances = 3, mergeNear = true } = {}) {
  const byKey = new Map();
  let skipped = 0;

  for (const call of calls || []) {
    const shape = skeletonizeShell(call.command);
    if (!shape.core) { skipped += 1; continue; }
    const key = skeletonKey(shape.core);
    let c = byKey.get(key);
    if (!c) {
      c = {
        key,
        skeleton: shape.core,
        programs: shape.programs,
        embedded: shape.statements.find((s) => s.embedded)?.embedded || null,
        instances: [],
        count: 0,
        sessions: new Set(),
        endpoints: new Set(),
        tenants: new Set(),
        mutates: false,
        egress: false,
        firstTs: null,
        lastTs: null,
        slots: new Map(),
      };
      byKey.set(key, c);
    }
    // The values this call's placeholders replaced, folded into per-placeholder
    // distributions. This is what keeps the skeleton honest: the shape says two
    // calls are one behaviour, the slot spread says how much that claim hides.
    for (const { ph, value } of shape.slotValues || []) addSlotValue(c.slots, ph, value, 1);
    c.count += 1;
    // foldMutates, not `||`: `false || null` is null in JS but `null || false`
    // is false, so a boolean OR makes the answer depend on arrival order and
    // silently downgrades "undecidable" to "read-only".
    c.mutates = foldMutates([c.mutates, shape.mutates]);
    c.egress = c.egress || shape.egress;
    if (call.sessionId) c.sessions.add(call.sessionId);
    if (call.endpointId) c.endpoints.add(call.endpointId);
    if (call.tenantId) c.tenants.add(call.tenantId);
    const t = call.ts ? Date.parse(call.ts) : NaN;
    if (!Number.isNaN(t)) {
      c.firstTs = c.firstTs === null ? t : Math.min(c.firstTs, t);
      c.lastTs = c.lastTs === null ? t : Math.max(c.lastTs, t);
    }
    // Keep RAW instances, not counts. Every wrong call this pipeline has made
    // came from reading an aggregate instead of an example, so the judge is
    // never handed a cluster it cannot read.
    if (c.instances.length < MAX_INSTANCES_KEPT) {
      // `output` and `outcome` ride along because the judge decides from what came
      // BACK as much as from what was sent. Dropping them here meant the judging
      // payload could only ever show commands, and a judge shown only commands
      // invents the fields it imagines a result contains.
      c.instances.push({
        command: call.command, sessionId: call.sessionId, promptId: call.promptId, ts: call.ts,
        output: call.output ?? null, outcome: call.outcome ?? null,
      });
    }
  }

  if (mergeNear) mergeNearDuplicates(byKey);

  const all = [...byKey.values()].map(finalize);
  return {
    clusters: all.filter((c) => c.count >= minInstances).sort((a, b) => b.count - a.count),
    tail: all.filter((c) => c.count < minInstances).sort((a, b) => b.count - a.count),
    totals: {
      calls: (calls || []).length,
      skipped,
      distinctSkeletons: all.length,
    },
  };
}

function addSlotValue(slots, ph, value, n) {
  let rec = slots.get(ph);
  if (!rec) { rec = { total: 0, values: new Map(), truncated: false }; slots.set(ph, rec); }
  rec.total += n;
  const have = rec.values.get(value);
  if (have !== undefined) rec.values.set(value, have + n);
  else if (rec.values.size < MAX_SLOT_VALUES) rec.values.set(value, n);
  else rec.truncated = true;
}

function finalize(c) {
  return {
    key: c.key,
    skeleton: c.skeleton,
    programs: c.programs,
    embedded: c.embedded,
    count: c.count,
    sessions: c.sessions.size,
    endpoints: c.endpoints.size,
    tenants: c.tenants.size,
    // Instances per session. High means a campaign (repeated within a task),
    // low means a habit (spread across many tasks).
    concentration: c.sessions.size ? Number((c.count / c.sessions.size).toFixed(2)) : c.count,
    consequence: { mutates: c.mutates, egress: c.egress },
    span: c.firstTs && c.lastTs ? { first: new Date(c.firstTs).toISOString(), last: new Date(c.lastTs).toISOString() } : null,
    instances: c.instances,
    // Per-placeholder value distributions, busiest slot first. The evidence for
    // whether ONE skeleton is one act or many wearing the same shape — a judge
    // (or a reader) sees the spread instead of extrapolating from five samples.
    slots: [...c.slots.entries()]
      .map(([ph, r]) => ({
        ph,
        total: r.total,
        distinct: r.values.size,
        truncated: r.truncated,
        top: [...r.values.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, TOP_SLOT_VALUES)
          .map(([value, n]) => ({ value, n })),
      }))
      .sort((a, b) => b.total - a.total),
  };
}

// Near-duplicate merge. Exact-hash clustering is precise but brittle: one extra
// flag forks a behaviour in two. Folding skeletons that differ by a single
// token recovers those without the unpredictability of a similarity threshold.
// Small clusters fold into larger ones, never the reverse, so the surviving
// skeleton is the more commonly written form.
function mergeNearDuplicates(byKey) {
  const order = [...byKey.values()].sort((a, b) => b.count - a.count);
  const index = new Map();

  for (const c of order) {
    const toks = c.skeleton.split(/\s+/);
    const variants = neighbourhood(toks);
    let target = null;
    for (const v of variants) {
      const owner = index.get(v);
      if (owner && owner !== c && byKey.has(owner.key)) { target = owner; break; }
    }
    if (target) { absorb(target, c); byKey.delete(c.key); continue; }
    // Index the cluster's OWN shape as well as its neighbourhood, so a later
    // cluster whose reduction equals this exact skeleton finds it.
    if (!index.has(c.skeleton)) index.set(c.skeleton, c);
    for (const v of variants) if (!index.has(v)) index.set(v, c);
  }
}

// The skeletons this one is a near-duplicate of: every single-token deletion,
// plus every flag-with-its-value deletion. The second case is not optional —
// an added flag that takes an argument differs by TWO tokens, which a
// single-deletion index cannot see, and `-y <VAL>` style options are the most
// common way one behaviour picks up an extra knob. Widening to a blanket
// two-token distance instead would over-merge unrelated shapes.
function neighbourhood(toks) {
  if (toks.length <= 2) return [toks.join(" ")];
  const out = [];
  for (let i = 0; i < toks.length; i += 1) {
    out.push(toks.filter((_, j) => j !== i).join(" "));
  }
  for (let i = 0; i < toks.length - 1; i += 1) {
    if (!toks[i].startsWith("-") || toks[i + 1].startsWith("-")) continue;
    out.push(toks.filter((_, j) => j !== i && j !== i + 1).join(" "));
  }
  return out;
}

function absorb(target, c) {
  target.count += c.count;
  target.mutates = foldMutates([target.mutates, c.mutates]);
  target.egress = target.egress || c.egress;
  // Slot distributions fold by value, so a near-duplicate merge cannot make a
  // varied slot look concentrated — the counts add rather than reset.
  for (const [ph, rec] of c.slots) {
    for (const [value, n] of rec.values) addSlotValue(target.slots, ph, value, n);
    const merged = target.slots.get(ph);
    if (merged) {
      merged.truncated = merged.truncated || rec.truncated;
      // `total` counted only the enumerated values above; add back the ones the
      // absorbed cluster had already stopped enumerating.
      const enumerated = [...rec.values.values()].reduce((a, b) => a + b, 0);
      merged.total += rec.total - enumerated;
    }
  }
  for (const s of c.sessions) target.sessions.add(s);
  for (const e of c.endpoints) target.endpoints.add(e);
  for (const t of c.tenants) target.tenants.add(t);
  for (const inst of c.instances) {
    if (target.instances.length < MAX_INSTANCES_KEPT) target.instances.push(inst);
  }
  if (c.firstTs !== null) target.firstTs = target.firstTs === null ? c.firstTs : Math.min(target.firstTs, c.firstTs);
  if (c.lastTs !== null) target.lastTs = target.lastTs === null ? c.lastTs : Math.max(target.lastTs, c.lastTs);
}

/**
 * A plain-text read of a mining run, for a human or an agent deciding what to
 * look at next. Shows one instance per cluster inline, because a skeleton alone
 * is not readable evidence.
 */
export function formatClusters(result, { limit = 30 } = {}) {
  const { clusters, tail, totals } = result;
  const lines = [
    `${totals.calls} calls → ${totals.distinctSkeletons} distinct skeletons`
    + ` · ${clusters.length} clusters at/above threshold · ${tail.length} in the tail`
    + (totals.skipped ? ` · ${totals.skipped} unskeletonizable` : ""),
    "",
  ];
  for (const c of clusters.slice(0, limit)) {
    const flags = [
      c.embedded ? c.embedded : null,
      c.consequence.mutates === true ? "mutates" : c.consequence.mutates === null ? "consequence unknown" : null,
      c.consequence.egress ? "egress" : null,
    ].filter(Boolean).join(" ");
    lines.push(
      `${String(c.count).padStart(5)} × ${c.skeleton.slice(0, 150)}`,
      `        ${c.sessions} sess · ${c.endpoints} ep · ${c.tenants} tn · conc ${c.concentration}${flags ? ` · ${flags}` : ""}`,
      `        ← ${String(c.instances[0]?.command || "").replace(/\s+/g, " ").slice(0, 160)}`,
    );
  }
  if (clusters.length > limit) lines.push(`… ${clusters.length - limit} more clusters`);
  return lines.join("\n");
}
